import { forwardRef } from 'react';
import type { FC } from 'react';
import AntInput from 'antd/es/input';
import type {
  InputRef,
  InputProps as AntInputProps,
  TextAreaProps as AntTextAreaProps,
} from 'antd/es/input';
import { cn } from 'app/lib/utils';

export interface InputProps extends AntInputProps {}
export interface TextAreaProps extends AntTextAreaProps {}

// Same field chrome for every plain input; when an addon is present antd
// borders the whole group instead, so the inner input stays unstyled.
const defaultChrome = (hasError: boolean) =>
  cn(
    'w-full box-border rounded-md border px-[11px] py-[7px] focus:outline-none',
    hasError
      ? 'border-red-500 focus:ring-2 focus:ring-red-200'
      : 'border-[#D1D5DB]'
  );

/**
 * Text input façade over antd `Input`; bakes in default field chrome keyed off `status`.
 */
// Forwards the ref so wrappers like AutoComplete can focus the input
const InputBase = forwardRef<InputRef, InputProps>(function InputBase(
  { status, className, style, addonBefore, addonAfter, ...rest },
  ref
) {
  const hasAddon = addonBefore !== undefined || addonAfter !== undefined;
  return (
    <AntInput
      ref={ref}
      status={status}
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      className={cn(
        hasAddon ? 'w-full' : defaultChrome(status === 'error'),
        className
      )}
      style={{ boxSizing: 'border-box', ...style }}
      {...rest}
    />
  );
});

const TextArea: FC<TextAreaProps> = ({ status, className, style, ...rest }) => (
  <AntInput.TextArea
    status={status}
    className={cn(defaultChrome(status === 'error'), className)}
    style={{ boxSizing: 'border-box', ...style }}
    {...rest}
  />
);

export const Input = InputBase as typeof InputBase & {
  TextArea: typeof TextArea;
};
Input.TextArea = TextArea;

export default Input;
